/**
 * Alternate-key cadence model. A press only counts if it alternates with the previous
 * one; cadence is presses per second over a short window, smoothed. Pure functions over
 * sim time (seconds), so the same code runs in the browser and on the server.
 */
import { clamp } from './rng.js';

/** Beyond this no human is alternating; anything above is clamped. */
export const MAX_CADENCE = 14;
/** Window for the raw cadence estimate (s). */
export const CADENCE_WINDOW = 0.5;
/** No press for this long and cadence is treated as zero (you stopped). */
export const CADENCE_TIMEOUT = 0.45;

export type RunKey = 0 | 1;

export interface Cadence {
  /** Press times inside the recent window (sim seconds). */
  times: number[];
  lastKey: RunKey | -1;
  /** Smoothed presses per second. */
  value: number;
  /** Counted presses. */
  count: number;
  /** Presses rejected for repeating the same key. */
  rejected: number;
  /** Intervals between counted presses, for regularity analysis (bounded). */
  intervals: number[];
  lastPress: number;
}

export function createCadence(): Cadence {
  return { times: [], lastKey: -1, value: 0, count: 0, rejected: 0, intervals: [], lastPress: 0 };
}

export type PressResult = 'counted' | 'same-key';

export function recordPress(c: Cadence, key: RunKey, t: number): PressResult {
  if (c.lastKey === key) {
    c.rejected++;
    return 'same-key';
  }
  c.lastKey = key;
  if (c.count > 0 && c.intervals.length < 2000) c.intervals.push(t - c.lastPress);
  c.lastPress = t;
  c.count++;
  c.times.push(t);
  return 'counted';
}

/** Presses per second in the recent window, or 0 if the player has stopped. */
export function rawCadence(c: Cadence, now: number): number {
  while (c.times.length && c.times[0]! < now - CADENCE_WINDOW) c.times.shift();
  if (c.count === 0 || now - c.lastPress > CADENCE_TIMEOUT) return 0;
  return clamp(c.times.length / CADENCE_WINDOW, 0, MAX_CADENCE);
}

/** Advance the smoothed cadence by dt seconds. */
export function updateCadence(c: Cadence, now: number, dt: number): number {
  const raw = rawCadence(c, now);
  // Fast attack so the first presses register, slower release so the meter does not flicker.
  const k = raw > c.value ? 12 : 6;
  c.value += (raw - c.value) * (1 - Math.exp(-dt * k));
  if (c.value < 0.01) c.value = 0;
  return c.value;
}

export interface Regularity {
  /** Coefficient of variation of the inter-press intervals. */
  cv: number;
  intervals: number;
  /** Too regular for a human: likely a macro or turbo controller. */
  suspicious: boolean;
}

/** Humans jitter by well over 5%; a macro is metronomic. */
export const SUSPICIOUS_CV = 0.035;
export const MIN_INTERVALS_FOR_CHECK = 24;

export function regularity(intervals: number[]): Regularity {
  const n = intervals.length;
  if (n < 2) return { cv: 0, intervals: n, suspicious: false };
  let sum = 0;
  for (const x of intervals) sum += x;
  const mean = sum / n;
  if (mean <= 0) return { cv: 0, intervals: n, suspicious: false };
  let sq = 0;
  for (const x of intervals) sq += (x - mean) ** 2;
  const cv = Math.sqrt(sq / n) / mean;
  return { cv, intervals: n, suspicious: n >= MIN_INTERVALS_FOR_CHECK && cv < SUSPICIOUS_CV };
}
